import { useCallback, useContext, useState } from "react";
import { useDispatch } from "react-redux";

import { useSearchParams } from "routes/hooks";

import { SetAdvisor } from "data/advisor/actions";
import useCurrentUser from "data/advisor/useCurrentUser";
import { SetHub } from "data/hub/actions";
import useHub from "data/hub/useHub";
import { EditHub, SetHubPreferences } from "data/libs/hubs";

import { ToasterContext } from "scenes/Hubly/components/Workspace/Provider";

function useHubIntegrationSettings() {
  const [loading, setLoading] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const { fireSuccess, fireError } = useContext(ToasterContext);

  const hub = useHub();
  const user = useCurrentUser();
  const dispatch = useDispatch();

  const runToggle = useCallback(
    ({ getCurrent, request, onSuccess, messages }) => {
      setLoading(true);
      const current = Boolean(getCurrent());

      request(!current)
        .then((response) => {
          onSuccess({ next: !current, response: response });
          fireSuccess(
            `Successfully ${current ? "disabled" : "enabled"} ${
              messages.subject
            }`
          );
        })
        .catch((err) => {
          fireError(
            `Failed to ${current ? "disable" : "enable"} ${messages.subject}`,
            err
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [fireError, fireSuccess]
  );

  const toggleSaveNotesToCrm = useCallback(() => {
    runToggle({
      getCurrent: () => {
        return hub.logCompletedWorkflowsInCrm;
      },
      request: (next) => {
        return EditHub(hub.id, { logCompletedWorkflowsInCrm: next });
      },
      onSuccess: ({ response }) => {
        if (hub && response.id === hub.id) dispatch(SetHub(response));

        const advisorCopy = { ...user };
        const index = advisorCopy.hubs.findIndex((h) => {
          return h.id === response.id;
        });
        if (index >= 0) {
          advisorCopy.hubs[index] = response;
          dispatch(SetAdvisor(advisorCopy));
        }
      },
      messages: { subject: "save notes to CRM" },
    });
  }, [dispatch, hub, runToggle, user]);

  const toggleSyncMeetings = useCallback(() => {
    runToggle({
      getCurrent: () => {
        return hub?.preferences?.sync?.shouldSyncMeetings;
      },
      request: (next) => {
        return SetHubPreferences(hub.id, {
          key: "sync",
          value: { ...hub?.preferences?.sync, shouldSyncMeetings: next },
        });
      },
      onSuccess: ({ next }) => {
        dispatch(
          SetHub({
            ...hub,
            preferences: {
              ...hub.preferences,
              sync: { ...hub.preferences.sync, shouldSyncMeetings: next },
            },
          })
        );

        if (next) setSearchParams({ tab: "meetings" });
      },
      messages: { subject: "sync meetings" },
    });
  }, [dispatch, hub, runToggle, setSearchParams]);

  const toggleCrmPermissions = useCallback(() => {
    runToggle({
      getCurrent: () => {
        return hub.useCrmPermissions;
      },
      request: (next) => {
        return SetHubPreferences(hub.id, {
          key: "sync",
          value: { ...hub?.preferences?.sync, useCrmPermissions: next },
        });
      },
      onSuccess: ({ next }) => {
        dispatch(
          SetHub({
            ...hub,
            preferences: {
              ...hub.preferences,
              sync: { ...hub?.preferences?.sync, useCrmPermissions: next },
            },
            useCrmPermissions: next,
          })
        );
      },
      messages: { subject: "CRM permissions" },
    });
  }, [dispatch, hub, runToggle]);

  return {
    loading: loading,
    toggle: {
      notes: toggleSaveNotesToCrm,
      meetings: toggleSyncMeetings,
      crmPermissions: toggleCrmPermissions,
    },
  };
}

export default useHubIntegrationSettings;
